// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Drag- and keyboard-resize for the right-docked Asset Details panel: the shared
 * `useDockedPanelResize` gesture bound to this panel's width, bounds and CSS
 * variable. The Ask Helios panel binds the same hook to its own.
 */

import {
  ASK_HELIOS_MAX_WIDTH,
  ASK_HELIOS_MIN_WIDTH,
  ASSET_PANEL_MAX_WIDTH,
  ASSET_PANEL_MIN_WIDTH,
  useShellStore,
} from '../stores/shellStore';
import { useDockedPanelResize, type UseDockedPanelResizeResult } from './useDockedPanelResize';

/** The CSS variable `.kg-panel-host` reads its width from. */
export const ASSET_PANEL_WIDTH_VAR = '--kg-panel-width';

export const useAssetPanelResize = (): UseDockedPanelResizeResult => {
  const askHeliosOpen = useShellStore(s => s.askHeliosOpen);
  const askHeliosWidth = useShellStore(s => s.askHeliosWidth);

  // This panel is the inboard one: when Ask Helios is open the host shifts it
  // left by that panel's width, so Ask Helios sits between it and the viewport
  // edge. Bounded here as well as in the store, because a rehydrated width is
  // only clamped against its own maximum.
  const offsetRight = askHeliosOpen
    ? Math.min(ASK_HELIOS_MAX_WIDTH, Math.max(ASK_HELIOS_MIN_WIDTH, askHeliosWidth))
    : 0;

  return useDockedPanelResize({
    widthVar: ASSET_PANEL_WIDTH_VAR,
    width: useShellStore(s => s.assetPanelWidth),
    setWidth: useShellStore(s => s.setAssetPanelWidth),
    minWidth: ASSET_PANEL_MIN_WIDTH,
    maxWidth: ASSET_PANEL_MAX_WIDTH,
    offsetRight,
    label: 'Resize asset details panel',
  });
};
